// src/pages/customer-panel/courses/CourseDetailPage.js
// Course learning page - Module navigation + content area

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Collapse,
    Checkbox,
    LinearProgress,
    Button,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Snackbar,
    Tooltip,
    useMediaQuery,
    Drawer,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ArticleIcon from '@mui/icons-material/Article';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SchoolIcon from '@mui/icons-material/School';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';
const SIDEBAR_WIDTH = 340;

function getModuleIcon(type) {
    switch ((type || '').toLowerCase()) {
        case 'video':
            return <PlayCircleFilledIcon sx={{ color: '#3b82f6' }} />;
        case 'quiz':
        case 'test':
            return <QuizIcon sx={{ color: '#f59e0b' }} />;
        case 'text':
        case 'article':
        default:
            return <ArticleIcon sx={{ color: '#8b5cf6' }} />;
    }
}

// Video content placeholder
const VideoContent = ({ module: mod }) => (
    <Box>
        <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2 }}>
            {mod.title}
        </Typography>
        <Paper
            sx={{
                width: '100%',
                aspectRatio: '16/9',
                bgcolor: '#0f172a',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                overflow: 'hidden',
            }}
        >
            {mod.video_url || mod.content_url ? (
                <>
                    <PlayCircleFilledIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.7)', mb: 2 }} />
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                        Video Player
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily,
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: '0.75rem',
                            mt: 1,
                            px: 3,
                            textAlign: 'center',
                            wordBreak: 'break-all',
                        }}
                    >
                        {mod.video_url || mod.content_url}
                    </Typography>
                </>
            ) : (
                <>
                    <PlayCircleFilledIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.4)' }}>
                        No video URL available
                    </Typography>
                </>
            )}
        </Paper>
        {mod.description && (
            <Typography sx={{ fontFamily, color: '#475569', lineHeight: 1.7 }}>
                {mod.description}
            </Typography>
        )}
    </Box>
);

// Text / article content
const TextContent = ({ module: mod }) => (
    <Box>
        <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 3 }}>
            {mod.title}
        </Typography>
        {mod.content || mod.html_content || mod.body ? (
            <Box
                sx={{
                    fontFamily,
                    color: '#334155',
                    lineHeight: 1.8,
                    '& h1, & h2, & h3, & h4': { fontFamily, color: '#1e293b', fontWeight: 700 },
                    '& p': { mb: 2 },
                    '& ul, & ol': { pl: 3, mb: 2 },
                    '& a': { color: ACCENT },
                    '& img': { maxWidth: '100%', borderRadius: '12px' },
                    '& code': {
                        bgcolor: '#f1f5f9',
                        px: 0.8,
                        py: 0.3,
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                    },
                    '& pre': {
                        bgcolor: '#1e293b',
                        color: '#e2e8f0',
                        p: 2.5,
                        borderRadius: '12px',
                        overflow: 'auto',
                        mb: 2,
                    },
                }}
                dangerouslySetInnerHTML={{ __html: mod.content || mod.html_content || mod.body }}
            />
        ) : (
            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No content available for this module.</Typography>
        )}
    </Box>
);

// Quiz / test content
const QuizContent = ({ module: mod, onSubmitQuiz }) => {
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [score, setScore] = useState(null);

    const questions = mod.questions || mod.quiz_questions || [];

    const handleAnswerChange = (questionId, value) => {
        if (!submitted) {
            setAnswers((prev) => ({ ...prev, [questionId]: value }));
        }
    };

    const handleSubmit = () => {
        setSubmitted(true);
        // Calculate score locally if correct answers available
        let correct = 0;
        questions.forEach((q) => {
            const qId = q.id || q._id;
            if (q.correct_answer !== undefined && answers[qId] === q.correct_answer) {
                correct++;
            }
        });
        const s = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
        setScore(s);
        if (onSubmitQuiz) {
            onSubmitQuiz(mod.id || mod._id, answers);
        }
    };

    if (questions.length === 0) {
        return (
            <Box>
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2 }}>
                    {mod.title}
                </Typography>
                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No quiz questions available.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                {mod.title}
            </Typography>
            <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                {questions.length} question{questions.length !== 1 ? 's' : ''}
            </Typography>

            {submitted && score !== null && (
                <Alert
                    severity={score >= 70 ? 'success' : 'warning'}
                    sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                >
                    You scored {score}% ({Math.round((score / 100) * questions.length)}/{questions.length} correct)
                </Alert>
            )}

            {questions.map((q, qIndex) => {
                const qId = q.id || q._id || `q-${qIndex}`;
                const options = q.options || q.choices || [];
                return (
                    <Paper
                        key={qId}
                        sx={{
                            p: 3,
                            mb: 2,
                            borderRadius: '14px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', mb: 2 }}>
                            {qIndex + 1}. {q.question || q.text || q.title}
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={answers[qId] || ''}
                                onChange={(e) => handleAnswerChange(qId, e.target.value)}
                            >
                                {options.map((opt, oIndex) => {
                                    const optValue = typeof opt === 'string' ? opt : (opt.value || opt.text || opt.label);
                                    const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.text || opt.value);
                                    return (
                                        <FormControlLabel
                                            key={oIndex}
                                            value={optValue}
                                            control={<Radio sx={{ '&.Mui-checked': { color: ACCENT } }} />}
                                            label={
                                                <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                                                    {optLabel}
                                                </Typography>
                                            }
                                            disabled={submitted}
                                            sx={{ mb: 0.5 }}
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                    </Paper>
                );
            })}

            {!submitted && (
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={Object.keys(answers).length < questions.length}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        bgcolor: ACCENT,
                        borderRadius: '12px',
                        px: 4,
                        py: 1.2,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    Submit Quiz
                </Button>
            )}
        </Box>
    );
};

export default function CourseDetailPage() {
    const { courseIdentity } = useParams();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:960px)');

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeModuleId, setActiveModuleId] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [completingId, setCompletingId] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchCourse = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get(`/lms/portal/courses/${courseIdentity}`);
            const data = response.data?.course || response.data?.data || response.data;
            setCourse(data);

            // Set first module active
            const sections = data.sections || data.modules_grouped || [];
            const flatModules = data.modules || [];
            if (sections.length > 0 && sections[0].modules?.length > 0) {
                setActiveModuleId(sections[0].modules[0].id || sections[0].modules[0]._id);
                // Expand first section
                setExpandedSections({ 0: true });
            } else if (flatModules.length > 0) {
                setActiveModuleId(flatModules[0].id || flatModules[0]._id);
            }
        } catch (err) {
            console.error('Failed to fetch course:', err);
            setError(err.response?.data?.message || 'Failed to load course details.');
        } finally {
            setLoading(false);
        }
    }, [courseIdentity]);

    useEffect(() => {
        fetchCourse();
    }, [fetchCourse]);

    // Flatten all modules for navigation
    const getAllModules = useCallback(() => {
        if (!course) return [];
        const sections = course.sections || course.modules_grouped || [];
        if (sections.length > 0) {
            return sections.reduce((acc, sec) => [...acc, ...(sec.modules || [])], []);
        }
        return course.modules || [];
    }, [course]);

    const allModules = getAllModules();

    const activeModule = allModules.find(
        (m) => (m.id || m._id) === activeModuleId
    );

    const activeModuleIndex = allModules.findIndex(
        (m) => (m.id || m._id) === activeModuleId
    );

    // Calculate progress
    const completedCount = allModules.filter(
        (m) => m.is_completed || m.completed
    ).length;
    const progressPercent = allModules.length > 0
        ? Math.round((completedCount / allModules.length) * 100)
        : 0;

    const handleToggleSection = useCallback((index) => {
        setExpandedSections((prev) => ({ ...prev, [index]: !prev[index] }));
    }, []);

    const handleMarkComplete = useCallback(async (moduleId) => {
        setCompletingId(moduleId);
        try {
            await axiosInstance.post(`/lms/portal/modules/${moduleId}/complete`);
            // Update local state
            setCourse((prev) => {
                if (!prev) return prev;
                const updated = JSON.parse(JSON.stringify(prev));
                const sections = updated.sections || updated.modules_grouped || [];
                if (sections.length > 0) {
                    sections.forEach((sec) => {
                        (sec.modules || []).forEach((m) => {
                            if ((m.id || m._id) === moduleId) {
                                m.is_completed = true;
                                m.completed = true;
                            }
                        });
                    });
                }
                const flatMods = updated.modules || [];
                flatMods.forEach((m) => {
                    if ((m.id || m._id) === moduleId) {
                        m.is_completed = true;
                        m.completed = true;
                    }
                });
                return updated;
            });
            setSnackbar({ open: true, message: 'Module marked as complete!', severity: 'success' });
        } catch (err) {
            console.error('Failed to mark module complete:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to mark module as complete.',
                severity: 'error',
            });
        } finally {
            setCompletingId(null);
        }
    }, []);

    const handleNext = useCallback(() => {
        if (activeModuleIndex < allModules.length - 1) {
            const nextMod = allModules[activeModuleIndex + 1];
            setActiveModuleId(nextMod.id || nextMod._id);
        }
    }, [activeModuleIndex, allModules]);

    const handlePrev = useCallback(() => {
        if (activeModuleIndex > 0) {
            const prevMod = allModules[activeModuleIndex - 1];
            setActiveModuleId(prevMod.id || prevMod._id);
        }
    }, [activeModuleIndex, allModules]);

    // Loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Alert
                    severity="error"
                    sx={{ borderRadius: '12px', fontFamily }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchCourse} sx={{ fontFamily, textTransform: 'none' }}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/courses')}
                    sx={{ fontFamily, textTransform: 'none', mt: 2, color: '#64748b' }}
                >
                    Back to Courses
                </Button>
            </Box>
        );
    }

    if (!course) return null;

    const sections = course.sections || course.modules_grouped || [];
    const hasSections = sections.length > 0;

    // Sidebar content
    const sidebarContent = (
        <Box
            sx={{
                width: SIDEBAR_WIDTH,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'white',
            }}
        >
            {/* Course Title in sidebar */}
            <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}
                    >
                        {course.title}
                    </Typography>
                    {isMobile && (
                        <IconButton size="small" onClick={() => setSidebarOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontFamily, color: '#64748b', fontWeight: 600 }}>
                        {completedCount}/{allModules.length} completed
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily, color: ACCENT, fontWeight: 700 }}>
                        {progressPercent}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: ACCENT,
                            borderRadius: 3,
                        },
                    }}
                />
            </Box>

            {/* Module List */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {hasSections ? (
                    sections.map((section, sIndex) => (
                        <Box key={section.id || section._id || sIndex}>
                            <Accordion
                                expanded={!!expandedSections[sIndex]}
                                onChange={() => handleToggleSection(sIndex)}
                                disableGutters
                                elevation={0}
                                sx={{
                                    '&:before': { display: 'none' },
                                    bgcolor: 'transparent',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                                    sx={{
                                        px: 2.5,
                                        '& .MuiAccordionSummary-content': { my: 1.5 },
                                        borderBottom: '1px solid #f1f5f9',
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}
                                        >
                                            {section.title || `Section ${sIndex + 1}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                            {(section.modules || []).filter((m) => m.is_completed || m.completed).length}/
                                            {(section.modules || []).length} lessons
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <List dense disablePadding>
                                        {(section.modules || []).map((mod) => {
                                            const modId = mod.id || mod._id;
                                            const isActive = modId === activeModuleId;
                                            const isComplete = mod.is_completed || mod.completed;
                                            return (
                                                <ListItemButton
                                                    key={modId}
                                                    selected={isActive}
                                                    onClick={() => {
                                                        setActiveModuleId(modId);
                                                        if (isMobile) setSidebarOpen(false);
                                                    }}
                                                    sx={{
                                                        px: 2.5,
                                                        py: 1.2,
                                                        borderLeft: isActive ? `3px solid ${ACCENT}` : '3px solid transparent',
                                                        bgcolor: isActive ? `${ACCENT}08` : 'transparent',
                                                        '&:hover': { bgcolor: isActive ? `${ACCENT}12` : '#f8fafc' },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        {isComplete ? (
                                                            <CheckCircleIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                                        ) : (
                                                            getModuleIcon(mod.type || mod.content_type)
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={mod.title}
                                                        primaryTypographyProps={{
                                                            fontFamily,
                                                            fontSize: '0.82rem',
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isComplete ? '#64748b' : '#1e293b',
                                                            sx: {
                                                                textDecoration: isComplete ? 'line-through' : 'none',
                                                            },
                                                        }}
                                                        secondary={mod.duration || null}
                                                        secondaryTypographyProps={{
                                                            fontFamily,
                                                            fontSize: '0.7rem',
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    ))
                ) : (
                    <List dense disablePadding>
                        {allModules.map((mod) => {
                            const modId = mod.id || mod._id;
                            const isActive = modId === activeModuleId;
                            const isComplete = mod.is_completed || mod.completed;
                            return (
                                <ListItemButton
                                    key={modId}
                                    selected={isActive}
                                    onClick={() => {
                                        setActiveModuleId(modId);
                                        if (isMobile) setSidebarOpen(false);
                                    }}
                                    sx={{
                                        px: 2.5,
                                        py: 1.2,
                                        borderLeft: isActive ? `3px solid ${ACCENT}` : '3px solid transparent',
                                        bgcolor: isActive ? `${ACCENT}08` : 'transparent',
                                        '&:hover': { bgcolor: isActive ? `${ACCENT}12` : '#f8fafc' },
                                        borderBottom: '1px solid #f1f5f9',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {isComplete ? (
                                            <CheckCircleIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                        ) : (
                                            getModuleIcon(mod.type || mod.content_type)
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={mod.title}
                                        primaryTypographyProps={{
                                            fontFamily,
                                            fontSize: '0.82rem',
                                            fontWeight: isActive ? 600 : 400,
                                            color: isComplete ? '#64748b' : '#1e293b',
                                            sx: {
                                                textDecoration: isComplete ? 'line-through' : 'none',
                                            },
                                        }}
                                        secondary={mod.duration || null}
                                        secondaryTypographyProps={{
                                            fontFamily,
                                            fontSize: '0.7rem',
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}
            </Box>
        </Box>
    );

    // Render active module content
    const renderModuleContent = () => {
        if (!activeModule) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <SchoolIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontFamily, color: '#64748b' }}>
                        Select a module from the sidebar to begin learning
                    </Typography>
                </Box>
            );
        }

        const moduleType = (activeModule.type || activeModule.content_type || 'text').toLowerCase();

        switch (moduleType) {
            case 'video':
                return <VideoContent module={activeModule} />;
            case 'quiz':
            case 'test':
                return <QuizContent module={activeModule} />;
            case 'text':
            case 'article':
            default:
                return <TextContent module={activeModule} />;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
            {/* Top Bar */}
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid #e2e8f0',
                    bgcolor: 'white',
                    zIndex: 10,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Back to Courses">
                        <IconButton onClick={() => navigate('/customer-panel/courses')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    {isMobile && (
                        <IconButton onClick={() => setSidebarOpen(true)} size="small">
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            color: '#475569',
                            maxWidth: { xs: 150, sm: 300, md: 500 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {course.title}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, minWidth: 160 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progressPercent}
                            sx={{
                                flexGrow: 1,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: '#e2e8f0',
                                '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 },
                            }}
                        />
                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 700, color: ACCENT }}>
                            {progressPercent}%
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Main layout */}
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* Sidebar - Desktop */}
                {!isMobile && (
                    <Paper
                        elevation={0}
                        sx={{
                            width: SIDEBAR_WIDTH,
                            flexShrink: 0,
                            borderRight: '1px solid #e2e8f0',
                            overflow: 'hidden',
                            display: 'flex',
                        }}
                    >
                        {sidebarContent}
                    </Paper>
                )}

                {/* Sidebar - Mobile Drawer */}
                {isMobile && (
                    <Drawer
                        open={sidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                        sx={{
                            '& .MuiDrawer-paper': {
                                width: Math.min(SIDEBAR_WIDTH, 320),
                            },
                        }}
                    >
                        {sidebarContent}
                    </Drawer>
                )}

                {/* Content Area */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        p: { xs: 2, md: 4 },
                    }}
                >
                    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                        {renderModuleContent()}

                        {/* Navigation + Mark Complete */}
                        {activeModule && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 4,
                                    pt: 3,
                                    borderTop: '1px solid #e2e8f0',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Button
                                    startIcon={<NavigateBeforeIcon />}
                                    disabled={activeModuleIndex <= 0}
                                    onClick={handlePrev}
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        color: '#64748b',
                                        borderRadius: '10px',
                                    }}
                                >
                                    Previous
                                </Button>

                                {!(activeModule.is_completed || activeModule.completed) && (
                                    <Button
                                        variant="contained"
                                        startIcon={
                                            completingId === (activeModule.id || activeModule._id) ? (
                                                <CircularProgress size={16} sx={{ color: 'white' }} />
                                            ) : (
                                                <CheckCircleIcon />
                                            )
                                        }
                                        disabled={completingId === (activeModule.id || activeModule._id)}
                                        onClick={() => handleMarkComplete(activeModule.id || activeModule._id)}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            bgcolor: ACCENT,
                                            borderRadius: '12px',
                                            px: 3,
                                            boxShadow: 'none',
                                            '&:hover': { bgcolor: '#059669' },
                                        }}
                                    >
                                        Mark as Complete
                                    </Button>
                                )}

                                {(activeModule.is_completed || activeModule.completed) && (
                                    <Chip
                                        icon={<CheckCircleIcon />}
                                        label="Completed"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            bgcolor: `${ACCENT}15`,
                                            color: ACCENT,
                                            '& .MuiChip-icon': { color: ACCENT },
                                        }}
                                    />
                                )}

                                <Button
                                    endIcon={<NavigateNextIcon />}
                                    disabled={activeModuleIndex >= allModules.length - 1}
                                    onClick={handleNext}
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        color: ACCENT,
                                        borderRadius: '10px',
                                    }}
                                >
                                    Next
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '12px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
